// hooks/useDeliveryTime.ts
import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { DeliveryType } from "../../../types/enums";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { DaySchedule, StoreInterface } from "../../../types/types";

interface UseDeliveryTimeProps {
  deliveryType: DeliveryType;
  selectedStore?: StoreInterface | null;
}

const getDayOfWeek = (date: Date): number => {
  const jsDay = date.getDay();
  if (jsDay === 0) return 6;
  return jsDay - 1;
};

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const formatDateTime = (date: Date): string => {
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const useDeliveryTime = ({
  deliveryType,
  selectedStore,
}: UseDeliveryTimeProps) => {
  const currentCluster = useSelector(selectCurrentCluster);
  const [desiredTime, setDesiredTime] = useState<string | null>(null);
  const [pickerVisible, setPickerVisible] = useState(false);

  // ✅ Расписание: для PICKUP — точки, для DELIVERY — кластера
  const schedule = useMemo((): DaySchedule[] => {
    if (deliveryType === DeliveryType.PICKUP) {
      return selectedStore?.schedule || [];
    }
    return currentCluster?.schedule || [];
  }, [deliveryType, selectedStore?.schedule, currentCluster?.schedule]);

  const getScheduleForDate = useCallback(
    (date: Date): DaySchedule | undefined => {
      const dayOfWeek = getDayOfWeek(date);
      return schedule.find((s) => s.sequence === dayOfWeek);
    },
    [schedule],
  );

  const isTimeAvailable = useCallback(
    (date: Date): boolean => {
      const daySchedule = getScheduleForDate(date);
      if (!daySchedule || daySchedule.dayOff) return false;

      const timeInMinutes = date.getHours() * 60 + date.getMinutes();
      const fromMinutes = timeToMinutes(daySchedule.from);
      const toMinutes = timeToMinutes(daySchedule.to);

      if (toMinutes < fromMinutes) {
        return timeInMinutes >= fromMinutes || timeInMinutes <= toMinutes;
      }
      return timeInMinutes >= fromMinutes && timeInMinutes <= toMinutes;
    },
    [getScheduleForDate],
  );

  // ✅ Буфер: доставка — из кластера, самовывоз — из точки
  const bufferMinutes =
    deliveryType === DeliveryType.DELIVERY
      ? (currentCluster?.cutOff ?? 60)
      : (selectedStore?.cutOff ?? 30);

  const minimumDate = useMemo((): Date | null => {
    if (!schedule.length) return null;

    const bufferMs = bufferMinutes * 60 * 1000;
    const now = new Date();
    const nowPlusBuffer = new Date(now.getTime() + bufferMs);

    // 1. Проверяем сегодня
    const todaySchedule = getScheduleForDate(now);
    if (todaySchedule && !todaySchedule.dayOff) {
      const [fromH, fromM] = todaySchedule.from.split(":").map(Number);
      const openDate = new Date(now);
      openDate.setHours(fromH, fromM, 0, 0);
      const openPlusBuffer = new Date(openDate.getTime() + bufferMs);

      const candidate = new Date(
        Math.max(nowPlusBuffer.getTime(), openPlusBuffer.getTime()),
      );

      if (isTimeAvailable(candidate)) {
        return candidate;
      }
    }

    // 2. Если сегодня не выходит — ищем следующий рабочий день
    let nextDay = new Date(nowPlusBuffer);
    nextDay.setHours(0, 0, 0, 0);

    for (let i = 0; i < 14; i++) {
      const testDate = new Date(nextDay);
      testDate.setDate(nextDay.getDate() + i);

      const daySchedule = getScheduleForDate(testDate);
      if (daySchedule && !daySchedule.dayOff) {
        const [hours, minutes] = daySchedule.from.split(":").map(Number);
        testDate.setHours(hours, minutes, 0, 0);

        const openPlusBuffer = new Date(testDate.getTime() + bufferMs);

        if (openPlusBuffer > now) {
          return openPlusBuffer;
        }
      }
    }

    return null;
  }, [isTimeAvailable, getScheduleForDate, schedule, bufferMinutes]);

  const displayText = desiredTime
    ? formatDateTime(new Date(desiredTime))
    : minimumDate
      ? `Ближайшее: ${formatDateTime(minimumDate)}`
      : "Нет доступного времени";

  const openPicker = useCallback(() => {
    setPickerVisible(true);
  }, []);

  const closePicker = useCallback(() => {
    setPickerVisible(false);
  }, []);

  const handleTimeSelect = useCallback(
    (date?: Date) => {
      setPickerVisible(false);
      if (date && isTimeAvailable(date)) {
        setDesiredTime(date.toISOString());
      }
    },
    [isTimeAvailable],
  );

  const clearTime = useCallback(() => {
    setDesiredTime(null);
  }, []);

  return {
    desiredTime,
    setDesiredTime,
    displayText,
    pickerVisible,
    openPicker,
    closePicker,
    handleTimeSelect,
    clearTime,
    minimumDate,
    isTimeAvailable,
    schedule,
  };
};
